import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import logo from '../assets/image.png';
import '../styles/animations.css';

const HomePage: React.FC = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", backgroundColor: '#0a0a0f' }}>
            <Navigation />
            
            {/* Hero Section */}
            <section style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #1a1a2e 100%)',
                backgroundSize: '400% 400%',
                animation: 'gradient-shift 15s ease infinite',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: '5rem'
            }}>
                {/* Neural Background */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 40%), radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 60%)',
                    animation: 'neural-pulse 8s ease-in-out infinite',
                    zIndex: 1
                }}></div>
                
                {/* Grid Pattern */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)',
                    backgroundSize: '60px 60px',
                    zIndex: 1
                }}></div>
                
                {/* Floating Orbs */}
                <div style={{
                    position: 'absolute',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)',
                    top: '10%',
                    left: '-10%',
                    animation: 'float 8s ease-in-out infinite',
                    filter: 'blur(40px)',
                    zIndex: 1
                }}></div>
                <div style={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
                    bottom: '20%',
                    right: '-5%',
                    animation: 'float 10s ease-in-out infinite reverse',
                    filter: 'blur(40px)',
                    zIndex: 1
                }}></div>

                {/* Hero Content */}
                <div style={{ 
                    maxWidth: '1280px', 
                    margin: '0 auto', 
                    position: 'relative', 
                    zIndex: 10,
                    textAlign: 'center',
                    padding: '2rem',
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(30px)',
                    transition: 'all 1s ease-out'
                }}>
                    {/* Logo with Glow */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '3rem' }}>
                        <div style={{ position: 'relative', animation: 'float 6s ease-in-out infinite' }}>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '120%',
                                height: '120%',
                                background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
                                filter: 'blur(30px)',
                                animation: 'pulse-glow 4s ease-in-out infinite',
                                zIndex: -1
                            }}></div>
                            <img 
                                src={logo} 
                                alt="Orbit AI Logo" 
                                loading="eager"
                                onLoad={() => setImageLoaded(true)}
                                style={{
                                    width: '320px',
                                    maxWidth: '80vw',
                                    height: 'auto',
                                    filter: 'drop-shadow(0 0 30px rgba(99, 102, 241, 0.4))',
                                    opacity: imageLoaded ? 1 : 0,
                                    transition: 'opacity 0.5s ease'
                                }}
                            />
                        </div>
                    </div>
                    
                    <h1 style={{ 
                        fontSize: 'clamp(2.5rem, 6vw, 4rem)', 
                        fontWeight: '800', 
                        marginBottom: '1.5rem',
                        color: 'white',
                        textShadow: '0 0 40px rgba(99, 102, 241, 0.3)',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1
                    }}>
                        AI Automation for <span style={{
                            background: 'linear-gradient(135deg, #818cf8, #a78bfa, #c4b5fd)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>Real Work</span>
                    </h1>
                    
                    <p style={{ 
                        fontSize: 'clamp(1.1rem, 2.5vw, 1.35rem)', 
                        color: 'rgba(255,255,255,0.8)',
                        maxWidth: '700px', 
                        margin: '0 auto 2.5rem',
                        lineHeight: 1.7
                    }}>
                        Orbit AI helps organizations automate governance, compliance, and everyday web tasks using configurable AI engines and natural language.
                    </p>
                    
                    {/* Product Badges */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '3rem', flexWrap: 'wrap' }}>
                        <span onClick={() => scrollToSection('governance-engine')} style={{ 
                            background: 'rgba(99, 102, 241, 0.2)', 
                            backdropFilter: 'blur(10px)', 
                            padding: '0.75rem 1.5rem', 
                            borderRadius: '9999px', 
                            fontSize: '0.9rem',
                            color: '#c4b5fd',
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}>
                            Governance Engine
                        </span>
                        <span onClick={() => scrollToSection('chrome-extension')} style={{ 
                            background: 'rgba(139, 92, 246, 0.2)', 
                            backdropFilter: 'blur(10px)', 
                            padding: '0.75rem 1.5rem', 
                            borderRadius: '9999px', 
                            fontSize: '0.9rem',
                            color: '#d8b4fe',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}>
                            Chrome Extension
                        </span>
                    </div>
                    
                    {/* CTA Buttons */}
                    <div style={{ display: 'flex', gap: '1.25rem', justifyContent: 'center', marginBottom: '3rem', flexWrap: 'wrap' }}>
                        <button onClick={() => scrollToSection('products')} style={{
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            color: 'white', 
                            padding: '1rem 2.5rem', 
                            borderRadius: '0.75rem',
                            fontWeight: '600', 
                            border: 'none', 
                            cursor: 'pointer', 
                            fontSize: '1.1rem',
                            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)'
                        }}>Explore Products</button>
                        <button onClick={() => scrollToSection('chrome-extension')} style={{
                            background: 'transparent', 
                            border: '2px solid rgba(255,255,255,0.4)', 
                            color: 'white', 
                            padding: '1rem 2.5rem',
                            borderRadius: '0.75rem', 
                            fontWeight: '600', 
                            cursor: 'pointer', 
                            fontSize: '1.1rem',
                            backdropFilter: 'blur(10px)'
                        }}>Install Extension</button>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap', color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>
                        <span>Privacy-focused</span>
                        <span>Works with existing tools</span>
                        <span>Bilingual (EN/AR)</span>
                    </div>
                </div>
                
                {/* Scroll Indicator */}
                <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', zIndex: 10, animation: 'float 2s ease-in-out infinite' }}>
                    <div style={{ width: '30px', height: '50px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '15px', display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
                        <div style={{ width: '6px', height: '10px', background: 'rgba(255,255,255,0.5)', borderRadius: '3px' }}></div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section id="products" style={{ padding: '6rem 1.5rem', background: 'linear-gradient(180deg, #0a0a1a 0%, #111827 100%)' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: '700', textAlign: 'center', marginBottom: '1rem', color: 'white' }}>Our Products</h2>
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 4rem' }}>Powerful AI solutions designed to transform how you work</p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '2rem', maxWidth: '900px', margin: '0 auto' }}>
                        <div onClick={() => scrollToSection('governance-engine')} style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)', borderRadius: '1.5rem', padding: '2.5rem', border: '1px solid rgba(99, 102, 241, 0.2)', cursor: 'pointer' }}>
                            <div style={{ width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            </div>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: 'white' }}>Governance Engine</h3>
                            <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem', lineHeight: '1.7' }}>AI-powered governance and compliance engine with configurable rule packs for your ERP and business data.</p>
                            <span style={{ color: '#a5b4fc', fontWeight: '600' }}>Learn More</span>
                        </div>
                        
                        <div onClick={() => scrollToSection('chrome-extension')} style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)', borderRadius: '1.5rem', padding: '2.5rem', border: '1px solid rgba(139, 92, 246, 0.2)', cursor: 'pointer' }}>
                            <div style={{ width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', boxShadow: '0 8px 20px rgba(139, 92, 246, 0.3)' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            </div>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: 'white' }}>Chrome Extension</h3>
                            <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem', lineHeight: '1.7' }}>Fill forms, extract data, and create charts on any website using simple English or Arabic commands.</p>
                            <span style={{ color: '#c4b5fd', fontWeight: '600' }}>Get Extension</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Governance Engine */}
            <section id="governance-engine" style={{ padding: '6rem 1.5rem', background: '#111827' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto 4rem', textAlign: 'center' }}>
                        <span style={{ background: 'rgba(99, 102, 241, 0.2)', color: '#a5b4fc', padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 500, display: 'inline-block' }}>GOVERNANCE ENGINE</span>
                        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: '700', marginTop: '1rem', marginBottom: '1rem', color: 'white' }}>One Engine, Multiple Scenarios</h2>
                        <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.6)' }}>A generic AI engine that adapts to your governance and compliance needs</p>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', maxWidth: '1100px', margin: '0 auto 4rem' }}>
                        {[
                            { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>, title: 'Generic Core', desc: 'A powerful rules engine that ingests normalized data from ERPs, HR, finance, and other systems.' },
                            { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, title: 'Pluggable Rules', desc: 'Enable rule packs for audit, payroll, tax/VAT, or asset lifecycle without changing core.' },
                            { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>, title: 'Flexible Deploy', desc: 'Runs on-premises or private cloud. Works with SQL Server and modern data stacks.' },
                            { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>, title: 'Clear Outcomes', desc: 'Detects anomalies, flags violations, generates explainable reports for decision-makers.' }
                        ].map((item, i) => (
                            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '1.25rem', padding: '2rem', border: '1px solid rgba(255,255,255,0.08)' }}>
                                <div style={{ width: '3.5rem', height: '3.5rem', background: 'rgba(99, 102, 241, 0.2)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem', color: '#a5b4fc' }}>{item.icon}</div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '0.75rem', color: 'white' }}>{item.title}</h3>
                                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', fontSize: '0.95rem' }}>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Chrome Extension */}
            <section id="chrome-extension" style={{ padding: '6rem 1.5rem', background: 'linear-gradient(180deg, #111827 0%, #0a0a1a 100%)' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto 4rem', textAlign: 'center' }}>
                        <span style={{ background: 'rgba(139, 92, 246, 0.2)', color: '#c4b5fd', padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 500, display: 'inline-block' }}>CHROME EXTENSION</span>
                        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: '700', marginTop: '1rem', marginBottom: '1rem', color: 'white' }}>Automate Web Tasks with AI</h2>
                        <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>Fill forms, extract data, and create charts using simple commands. No coding required.</p>
                        <button onClick={() => window.open('https://chrome.google.com/webstore', '_blank')} style={{ background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: 'white', padding: '1rem 2rem', borderRadius: '0.75rem', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '1rem', boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)' }}>Add to Chrome - Free</button>
                    </div>

                    <div style={{ maxWidth: '1000px', margin: '0 auto 5rem' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', textAlign: 'center', marginBottom: '3rem', color: 'white' }}>How It Works</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem' }}>
                            {[
                                { step: '1', title: 'Install', desc: 'Add OrbitAI from Chrome Web Store' },
                                { step: '2', title: 'Navigate', desc: 'Go to any website you want to automate' },
                                { step: '3', title: 'Command', desc: 'Type what you want in English or Arabic' },
                                { step: '4', title: 'Done', desc: 'AI executes your command instantly' }
                            ].map((item, i) => (
                                <div key={i} style={{ textAlign: 'center' }}>
                                    <div style={{ width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #10b981, #34d399)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '1.5rem', fontWeight: '700', color: 'white', boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)' }}>{item.step}</div>
                                    <h4 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '0.5rem', color: 'white' }}>{item.title}</h4>
                                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', textAlign: 'center', marginBottom: '3rem', color: 'white' }}>Features</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                            {[
                                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>, title: 'Form Automation', desc: 'Fill forms by describing what you want in plain language.' },
                                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>, title: 'Data Extraction', desc: 'Extract structured data from tables, lists, and any content.' },
                                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>, title: 'Chart Creation', desc: 'Generate visual charts from data on any webpage.' },
                                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>, title: 'Works Anywhere', desc: 'Compatible with any website, no integration needed.' },
                                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg>, title: 'Bilingual', desc: 'Full support for English and Arabic commands.' },
                                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, title: 'Privacy First', desc: 'Your data stays local. Nothing sent to external servers.' }
                            ].map((item, i) => (
                                <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '1rem', padding: '1.75rem', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    <span style={{ marginBottom: '1rem', display: 'block', color: '#c4b5fd' }}>{item.icon}</span>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.5rem', color: 'white' }}>{item.title}</h4>
                                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: '1.6' }}>{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{ background: '#050508', color: 'white', padding: '4rem 1.5rem 2rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto', textAlign: 'center' }}>
                    <img src={logo} alt="Orbit AI" style={{ width: '150px', height: 'auto', marginBottom: '1.5rem', opacity: 0.9 }} />
                    <p style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '500px', margin: '0 auto 1.5rem' }}>Dubai-based AI company specializing in custom AI applications and intelligent automation solutions.</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                        <button onClick={() => scrollToSection('products')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>Products</button>
                        <button onClick={() => scrollToSection('governance-engine')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>Governance</button>
                        <button onClick={() => scrollToSection('chrome-extension')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>Extension</button>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem' }}>
                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>2025 ORBIT AI. All rights reserved. | License No: 1223581</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePage;
